import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import { CalendarHeader } from 'components/calendar/CalendarHeader';
import { MonthView } from 'components/calendar/MonthView';
import { WeekView } from 'components/calendar/WeekView';
import { DayView } from 'components/calendar/DayView';
import PrjScheduleRegistPopup from 'pages/setup/popup/PrjScheduleRegistPopup';
import type { PrjSchedule, ViewMode } from 'models/setup/PrjSchedule';
import { findPrjScheduleList, savePrjSchedule, copyPrjSchedule } from 'apis/setup/PrjSchedule';
import { CalendarRoot, CalMainContainer } from 'components/calendar/Calendar.styles';

import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { findDisplayProject } from 'apis/setup/SetupProject';
import type { SetupProject } from 'models/setup/SetupProject';
import type { Code } from 'models/common/CommonCode';
import { buildCategoryThemes, EVENT_CATEGORIES, type CategoryTheme } from 'utils/CalendarUtils';
import { CrudCode } from 'models/common/Edit';
import type { CalendarProps } from 'pages/setup/PrjSchedulePage';
import { useTranslation } from 'react-i18next';
import { useMessageBar } from 'hooks/useMessageBar';
import useSessionStore from 'stores/useSessionStore';

export const PrjScheduleCalendarPage = ({
  setPageMode,
  viewMode: propViewMode,
  setViewMode: propSetViewMode,
}: CalendarProps) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { elmPrjId, langCd } = useSessionStore();
  dayjs.locale(langCd);

  const [internalViewMode, setInternalViewMode] = useState<ViewMode>('month');
  const viewMode = propViewMode ?? internalViewMode;
  const setViewMode = propSetViewMode ?? setInternalViewMode;

  // Initialize with August 2026 as reference base
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());

  const [schedules, setSchedules] = useState<PrjSchedule[]>([]);

  // 프로젝트 목록
  const [prjList, setPrjList] = useState<SetupProject[]>([]);

  // 검색조건 유형 공통코드 목록
  const [typeList, setTypeList] = useState<Code[]>([]);

  // 공통코드('SCHD_WKT_TP_CD')로부터 가져온 카테고리 테마 목록
  const [categories, setCategories] = useState<CategoryTheme[]>(EVENT_CATEGORIES);

  // 업무 유형 범례 토글 상태 (초기값: 전체 선택)
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set<string>(EVENT_CATEGORIES.map((t) => t.id))
  );

  // Event modal state
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<PrjSchedule | null>(null);
  const [defaultDateForAdd, setDefaultDateForAdd] = useState<string>(
    currentDate.format('YYYY-MM-DD HH:mm')
  );

  // 검색 조건 상태 (프로젝트, 협력업체, 유형)
  const [searchProject, setSearchProject] = useState(elmPrjId);
  const [searchExcp, setsearchExcp] = useState('');
  const [searchDelay, setSearchDelay] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    loadSchedulesFromApi();
  }, [currentDate, searchProject, searchExcp, searchDelay]);

  const filterSchedules = useMemo(() => {
    return schedules.filter((o) => selectedCategories.has(o.wktTpCd || ''));
  }, [schedules, selectedCategories]);

  const handleToggleCategory = (category: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const loadSchedulesFromApi = async () => {
    const apiData = await findPrjScheduleList({
      elmPrjId: searchProject,
      excpId: searchExcp,
      delayYn: searchDelay,
    });
    setSchedules(apiData);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [projectList, wktTpCdList] = await Promise.all([
          findDisplayProject(),
          getCommonCodeNames('SCHD_WKT_TP_CD'),
        ]);

        if (projectList) {
          setPrjList(projectList);
        }

        if (wktTpCdList && wktTpCdList.length > 0) {
          setTypeList(wktTpCdList);
          const themes = buildCategoryThemes(wktTpCdList);
          setCategories(themes);
          setSelectedCategories(new Set<string>(themes.map((t) => t.id)));
        }
      } catch (error) {
        console.error('Failed to load initial calendar setup data:', error);
      }
    };

    loadSchedulesFromApi();
    fetchInitialData();
  }, []);

  // Navigation handlers
  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate((prev) => prev.subtract(1, 'month'));
    } else if (viewMode === 'week') {
      setCurrentDate((prev) => prev.subtract(1, 'week'));
    } else {
      setCurrentDate((prev) => prev.subtract(1, 'day'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate((prev) => prev.add(1, 'month'));
    } else if (viewMode === 'week') {
      setCurrentDate((prev) => prev.add(1, 'week'));
    } else {
      setCurrentDate((prev) => prev.add(1, 'day'));
    }
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  // Add / Edit / Delete handlers
  const handleOpenAddModal = (dateStr?: string, timeStr?: string) => {
    setSelectedSchedule(null);
    const targetDate = dateStr || currentDate.format('YYYY-MM-DD');
    setDefaultDateForAdd(timeStr ? `${targetDate}T${timeStr}` : `${targetDate} 09:00`);
    setIsEventModalOpen(true);
  };

  const handleMove = () => {
    setPageMode?.('list');
  };

  const handleSelectSchedule = (schedule: PrjSchedule) => {
    setSelectedSchedule(schedule);
    setIsEventModalOpen(true);
  };

  // Drag & Drop event move handler
  const handleMoveScheduleDate = async (
    schdId: string,
    targetDateStr: string,
    targetTime?: string,
    isAllDay?: boolean
  ) => {
    const targetSchedule = schedules.find((e) => e.schdId === schdId);
    if (!targetSchedule) return;

    // 1. 완료된 항목은 drag/drop 불가
    if (targetSchedule.schdStatCd === 'CPLT') {
      return;
    }

    const origStart = dayjs(targetSchedule.planStDtm);
    const origEnd = dayjs(targetSchedule.planEndDtm);
    const durationMinutes = Math.max(origEnd.diff(origStart, 'minute'), 30);

    const startTime = isAllDay
      ? origStart.format('HH:mm:ss')
      : `${targetTime || origStart.format('HH:mm')}:00`;
    const nextStart = dayjs(`${targetDateStr} ${startTime}`);

    const newStartDtm = nextStart.format('YYYY-MM-DD HH:mm:ss');
    const newEndDtm = nextStart.add(durationMinutes, 'minute').format('YYYY-MM-DD HH:mm:ss');

    const previousSchedules = schedules;
    const isCopyMode = targetSchedule.schdProcYn === 'Y';

    // 원래 날짜 및 시간과 동일한 경우 아무런 동작을 하지 않음
    if (
      targetSchedule.planStDtm &&
      targetSchedule.planEndDtm &&
      dayjs(targetSchedule.planStDtm).isSame(dayjs(newStartDtm), 'minute') &&
      dayjs(targetSchedule.planEndDtm).isSame(dayjs(newEndDtm), 'minute')
    ) {
      return;
    }

    if (isCopyMode) {
      // 3. '!' 알람이 표시된 일정 drag/drop 시 일정을 복사하여 복사본을 수정하도록 처리
      const copiedSchedule: PrjSchedule = {
        ...targetSchedule,
        planStDtm: newStartDtm,
        planEndDtm: newEndDtm,
      };

      try {
        const res = await copyPrjSchedule(copiedSchedule);
        // 2. 에러가 발생하거나 성공하더라도 res.successOrNot !== 'Y' 이면 롤백 처리
        if (!res || res.successOrNot !== 'Y') {
          throw new Error('복사 저장 실패');
        }
        setSchedules((prev) => [...prev, copiedSchedule]);
        openMessageBar({
          type: 'confirm',
          content: t('com.msg.일정 복사본이 생성되었습니다.', '일정 복사본이 생성되었습니다.'),
        });

        // 저장 완료후 목록 새로고침
        loadSchedulesFromApi();
      } catch (error) {
        setSchedules(previousSchedules);
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.일정 복사에 실패하여 변경이 취소되었습니다.',
            '일정 복사에 실패하여 변경이 취소되었습니다.'
          ),
        });
      }
    } else {
      // 일반 일정 이동 처리
      const updatedSchedule: PrjSchedule = {
        ...targetSchedule,
        planStDtm: newStartDtm,
        planEndDtm: newEndDtm,
      };

      // 낙관적 UI 업데이트
      setSchedules((prev) => prev.map((o) => (o.schdId === schdId ? updatedSchedule : o)));

      try {
        const res = await savePrjSchedule({ ...updatedSchedule, crudKey: CrudCode.UPDATE });
        // 2. 에러가 발생하거나 성공하더라도 res.successOrNot !== 'Y' 이면 롤백 처리
        if (!res || res.successOrNot !== 'Y') {
          openMessageBar({
            type: 'error',
            content: t('com.msg.일정 변경에 실패하였습니다.', '일정 변경에 실패하였습니다.'),
          });
          return;
        }

        // 저장 완료후 목록 새로고침
        loadSchedulesFromApi();
      } catch (error) {
        setSchedules(previousSchedules);
        openMessageBar({
          type: 'error',
          content: t('com.msg.일정 변경에 실패하였습니다.', '일정 변경에 실패하였습니다.'),
        });
      }
    }
  };

  const handleClosePopup = () => {
    setIsEventModalOpen(false);
    loadSchedulesFromApi();
  };

  return (
    <CalendarRoot>
      {/* Calendar Top Navigation Header */}
      <CalendarHeader
        currentDate={currentDate}
        viewMode={viewMode}
        selectedCategories={selectedCategories}
        categories={categories}
        projectList={prjList}
        typeList={typeList}
        onToggleCategory={handleToggleCategory}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onViewModeChange={setViewMode}
        onMoveList={handleMove}
        onOpenAddModal={() => handleOpenAddModal()}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        searchProject={searchProject}
        onSearchProjectChange={setSearchProject}
        searchExcp={searchExcp}
        onsearchExcpChange={setsearchExcp}
        searchDelay={searchDelay}
        onSearchDelayChange={setSearchDelay}
      />

      {/* Main Calendar View Area */}
      <CalMainContainer>
        {viewMode === 'month' && (
          <MonthView
            currentDate={currentDate}
            schedules={filterSchedules}
            selectedCategories={selectedCategories}
            onSelectSchedule={handleSelectSchedule}
            isExpanded={isExpanded}
            onAddScheduleOnDate={handleOpenAddModal}
            onMoveScheduleDate={handleMoveScheduleDate}
          />
        )}
        {viewMode === 'week' && (
          <WeekView
            currentDate={currentDate}
            schedules={filterSchedules}
            selectedCategories={selectedCategories}
            onSelectSchedule={handleSelectSchedule}
            isExpanded={isExpanded}
            onAddScheduleOnDate={handleOpenAddModal}
            onMoveScheduleDate={handleMoveScheduleDate}
          />
        )}
        {viewMode === 'day' && (
          <DayView
            currentDate={currentDate}
            schedules={filterSchedules}
            selectedCategories={selectedCategories}
            onSelectSchedule={handleSelectSchedule}
            isExpanded={isExpanded}
            onAddScheduleOnDate={handleOpenAddModal}
            onMoveScheduleDate={handleMoveScheduleDate}
          />
        )}
      </CalMainContainer>

      {isEventModalOpen && (
        <PrjScheduleRegistPopup
          open={isEventModalOpen}
          close={handleClosePopup}
          schdId={selectedSchedule?.schdId}
          defaultDate={defaultDateForAdd}
        />
      )}
    </CalendarRoot>
  );
};

export default PrjScheduleCalendarPage;
